import React, { useState, useEffect } from 'react';
import { api, Post, PostType } from '../utils/api';
import { useLanguage } from '../contexts/LanguageContext';
import SubscribeWidget from '../components/SubscribeWidget';

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').trim();
}

type NewsTab = 'all' | 'association' | 'community' | 'education';

const fallbackNews: Post[] = [
  {
    id: 30, title: 'CAAOC Announces 2026 Board Elections', subtitle: 'Get Involved in Leadership',
    slug: 'board-elections-2026',
    content: 'The CAAOC board of directors is holding elections this May. We are seeking passionate community leaders to help shape the future of our organization. Nominations are now open for President, Vice President, Treasurer, and Secretary positions. All active members in good standing are eligible to run. Attend our information session on April 20th to learn more about the roles and responsibilities.',
    post_type: 'association', status: 'published', featured: 0, category: 'Association',
    published_at: '2026-03-28T00:00:00Z',
    author_id: 1, created_at: '2026-03-28T00:00:00Z', updated_at: '2026-03-28T00:00:00Z',
  },
  {
    id: 31, title: 'New Partnership with Irvine Public Library', subtitle: 'Expanding Educational Programs',
    slug: 'library-partnership',
    content: 'CAAOC has partnered with the Irvine Public Library system to offer expanded Chinese language and cultural programs. Starting this spring, we will offer weekly Mandarin conversation groups, children\'s bilingual storytime, and a new Chinese film series. These programs are free and open to the public. Visit the library website for more details and registration.',
    post_type: 'community', status: 'published', featured: 0, category: 'Community',
    published_at: '2026-03-25T00:00:00Z',
    author_id: 1, created_at: '2026-03-25T00:00:00Z', updated_at: '2026-03-25T00:00:00Z',
  },
  {
    id: 32, title: 'Scholarship Applications Now Open', subtitle: '2026-2027 Academic Year',
    slug: 'scholarship-2026',
    content: 'CAAOC is proud to announce that scholarship applications for the 2026-2027 academic year are now open. We offer scholarships ranging from $500 to $2,000 for high school seniors and college students of Chinese-American heritage in Orange County. Applications are due by May 31st. Please visit our website for eligibility requirements and application forms.',
    post_type: 'education', status: 'published', featured: 1, category: 'Education',
    published_at: '2026-03-20T00:00:00Z',
    author_id: 1, created_at: '2026-03-20T00:00:00Z', updated_at: '2026-03-20T00:00:00Z',
  },
  {
    id: 33, title: 'Volunteer Appreciation Dinner Recap', subtitle: 'Thank You to Our Amazing Volunteers',
    slug: 'volunteer-dinner',
    content: 'Last weekend, CAAOC hosted our annual Volunteer Appreciation Dinner to honor the dedicated individuals who make our programs possible. Over 80 volunteers were recognized for their contributions in 2025. Special awards were given to Outstanding Volunteer of the Year and Lifetime Service recipients. We are grateful for the incredible commitment of our volunteer community.',
    post_type: 'association', status: 'published', featured: 0, category: 'Association',
    published_at: '2026-03-15T00:00:00Z',
    author_id: 1, created_at: '2026-03-15T00:00:00Z', updated_at: '2026-03-15T00:00:00Z',
  },
  {
    id: 34, title: 'Orange County Asian Heritage Month Events', subtitle: 'May 2026 Community Calendar',
    slug: 'asian-heritage-month',
    content: 'May is Asian Heritage Month and Orange County has a packed calendar of events celebrating Asian-American culture and contributions. CAAOC is participating in the county-wide festival with performances, food vendors, and informational booths. Check our events page for the full schedule of CAAOC-hosted activities throughout the month.',
    post_type: 'community', status: 'published', featured: 0, category: 'Community',
    published_at: '2026-03-10T00:00:00Z',
    author_id: 1, created_at: '2026-03-10T00:00:00Z', updated_at: '2026-03-10T00:00:00Z',
  },
  {
    id: 35, title: 'Chinese Language School Registration Open', subtitle: 'Fall 2026 Enrollment',
    slug: 'language-school-registration',
    content: 'Registration for CAAOC Chinese Language School\'s Fall 2026 semester is now open. We offer Mandarin classes for all ages and levels, from beginner to advanced. Our curriculum includes reading, writing, speaking, and cultural appreciation. Classes are held on Saturdays at the Irvine Public Library. Early bird discount available through June 30th.',
    post_type: 'education', status: 'published', featured: 0, category: 'Education',
    published_at: '2026-03-05T00:00:00Z',
    author_id: 1, created_at: '2026-03-05T00:00:00Z', updated_at: '2026-03-05T00:00:00Z',
  },
];

function formatDate(dateStr?: string, locale?: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString(locale === 'zh' ? 'zh-CN' : 'en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function getTypeStyle(type: string, t: (key: string) => string): { color: string; bg: string; label: string } {
  switch (type) {
    case 'association':
      return { color: 'var(--red-primary)', bg: 'var(--red-bg)', label: t('news.association') };
    case 'community':
      return { color: 'var(--blue-accent)', bg: 'var(--blue-bg)', label: t('news.community') };
    case 'education':
      return { color: 'var(--green-accent)', bg: 'var(--green-bg)', label: t('news.education') };
    default:
      return { color: 'var(--gray-600)', bg: 'var(--gray-200)', label: t('news.allNews') };
  }
}

export default function NewsPage() {
  const { locale, t } = useLanguage();
  const [news, setNews] = useState<Post[]>(fallbackNews);
  const [activeTab, setActiveTab] = useState<NewsTab>('all');
  const [loading, setLoading] = useState(false);

  const tabConfig: { key: NewsTab; label: string; color: string; bg: string }[] = [
    { key: 'all', label: t('news.allNews'), color: 'var(--gray-700)', bg: 'var(--gray-200)' },
    { key: 'association', label: t('news.association'), color: 'var(--red-primary)', bg: 'var(--red-bg)' },
    { key: 'community', label: t('news.community'), color: 'var(--blue-accent)', bg: 'var(--blue-bg)' },
    { key: 'education', label: t('news.education'), color: 'var(--green-accent)', bg: 'var(--green-bg)' },
  ];

  useEffect(() => {
    setLoading(true);
    const localeParam = locale === 'zh' ? '&locale=zh' : '&locale=en';
    api.get<{ posts: Post[] }>(`/posts?status=published&limit=50${localeParam}`)
      .then((data) => {
        if (data.posts && data.posts.length > 0) {
          const newsItems = data.posts.filter(
            (p) => p.post_type === 'association' || p.post_type === 'community' || p.post_type === 'education'
          );
          if (newsItems.length > 0) setNews(newsItems);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [locale]);

  const filtered = activeTab === 'all' ? news : news.filter((n) => n.post_type === activeTab);

  // Separate featured from regular
  const featured = filtered.find((n) => n.featured);
  const regular = filtered.filter((n) => n !== featured);

  return (
    <div>
      {/* Hero */}
      <section
        style={{
          background: 'linear-gradient(135deg, #388E3C 0%, #1B5E20 100%)',
          color: '#fff',
          padding: '100px 0 60px',
        }}
      >
        <div className="container" style={{ textAlign: 'center' }}>
          <h1 style={{ fontSize: '2.8rem', fontWeight: 900, marginBottom: 12 }}>{t('news.title')}</h1>
          <p style={{ fontSize: '1.15rem', opacity: 0.9, maxWidth: 600, margin: '0 auto' }}>
            {t('news.subtitle')}
          </p>
        </div>
      </section>

      <section style={{ padding: 'var(--section-padding)', background: 'var(--gray-100)' }}>
        <div className="container">
          {/* Tabs */}
          <div
            style={{
              display: 'flex',
              gap: 8,
              marginBottom: 40,
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}
          >
            {tabConfig.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  padding: '10px 24px',
                  borderRadius: 24,
                  border: activeTab === tab.key ? `2px solid ${tab.color}` : 'none',
                  background: activeTab === tab.key ? tab.bg : 'var(--white)',
                  color: activeTab === tab.key ? tab.color : 'var(--gray-600)',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  boxShadow: 'var(--shadow-sm)',
                  transition: 'all 0.2s ease',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {loading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
              <div className="spinner" />
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <div style={{ textAlign: 'center', padding: 60, color: 'var(--gray-500)' }}>
              {t('news.noArticles')}
            </div>
          )}

          {!loading && (
            <div>
              {/* Featured article */}
              {featured && (
                <div
                  className="card"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: featured.featured_image_url ? '1fr 1fr' : '1fr',
                    gap: 32,
                    marginBottom: 32,
                    padding: 0,
                    overflow: 'hidden',
                  }}
                >
                  {featured.featured_image_url && (
                    <img
                      src={featured.featured_image_url}
                      alt={featured.title}
                      style={{ width: '100%', height: '100%', minHeight: 300, objectFit: 'cover' }}
                    />
                  )}
                  <div style={{ padding: 32, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                      <span
                        className="badge"
                        style={{
                          background: 'var(--red-bg)',
                          color: 'var(--red-primary)',
                        }}
                      >
                        {t('news.featured')}
                      </span>
                      <span
                        className="badge"
                        style={{
                          background: getTypeStyle(featured.post_type, t).bg,
                          color: getTypeStyle(featured.post_type, t).color,
                        }}
                      >
                        {getTypeStyle(featured.post_type, t).label}
                      </span>
                    </div>
                    <h2 style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--gray-900)', marginBottom: 8 }}>
                      {featured.title}
                    </h2>
                    {featured.subtitle && (
                      <p style={{ fontSize: '1.05rem', color: 'var(--gray-500)', marginBottom: 12, fontWeight: 500 }}>
                        {featured.subtitle}
                      </p>
                    )}
                    <p style={{ fontSize: '0.95rem', color: 'var(--gray-600)', lineHeight: 1.6, marginBottom: 16 }}>
                      {featured.content && stripHtml(featured.content).length > 250
                        ? stripHtml(featured.content).substring(0, 250) + '...'
                        : featured.content ? stripHtml(featured.content) : ''}
                    </p>
                    <div style={{ fontSize: '0.83rem', color: 'var(--gray-500)' }}>
                      {formatDate(featured.published_at || featured.created_at, locale)}
                    </div>
                  </div>
                </div>
              )}

              {/* Regular articles */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                  gap: 24,
                }}
              >
                {regular.map((article) => {
                  const typeStyle = getTypeStyle(article.post_type, t);
                  return (
                    <div key={article.id} className="card" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                      {article.featured_image_url && (
                        <img
                          src={article.featured_image_url}
                          alt={article.title}
                          style={{
                            width: '100%',
                            height: 180,
                            objectFit: 'cover',
                            borderRadius: 'var(--radius-sm)',
                          }}
                        />
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span className="badge" style={{ background: typeStyle.bg, color: typeStyle.color }}>
                          {typeStyle.label}
                        </span>
                        <span style={{ fontSize: '0.8rem', color: 'var(--gray-500)' }}>
                          {formatDate(article.published_at || article.created_at, locale)}
                        </span>
                      </div>
                      <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--gray-900)' }}>
                        {article.title}
                      </h3>
                      {article.subtitle && (
                        <p style={{ fontSize: '0.88rem', color: 'var(--gray-500)', fontWeight: 500 }}>
                          {article.subtitle}
                        </p>
                      )}
                      <p style={{ fontSize: '0.88rem', color: 'var(--gray-600)', lineHeight: 1.5, flex: 1 }}>
                        {article.content && stripHtml(article.content).length > 160
                          ? stripHtml(article.content).substring(0, 160) + '...'
                          : article.content ? stripHtml(article.content) : ''}
                      </p>
                      <button
                        style={{
                          alignSelf: 'flex-start',
                          background: 'none',
                          border: 'none',
                          color: typeStyle.color,
                          fontWeight: 600,
                          fontSize: '0.88rem',
                          padding: 0,
                          cursor: 'pointer',
                        }}
                      >
                        {t('news.readMore')} &rarr;
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Subscribe */}
      <section style={{ padding: '0 0 60px', background: 'var(--gray-100)' }}>
        <div className="container">
          <SubscribeWidget />
        </div>
      </section>
    </div>
  );
}
